import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useSeatPersonalization } from '@/hooks/useSeatPersonalization';
import { supabase } from '@/integrations/supabase/client';
import { UserMenu } from '@/components/UserMenu';
import { WorkspaceAvatar } from '@/components/WorkspaceAvatar';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { WelcomeDialog } from '@/components/dashboard/WelcomeDialog';
import { TeamManagementSheet } from '@/components/dashboard/TeamManagementSheet';
import { ActiveSessionsList } from '@/components/dashboard/ActiveSessionsList';
import { AttentionQueue } from '@/components/dashboard/AttentionQueue';
import { NewAssignmentsDialog } from '@/components/dashboard/NewAssignmentsDialog';
import { DashboardSidebar } from '@/components/dashboard/DashboardSidebar';
import { NewDecisionSheet } from '@/components/dashboard/NewDecisionSheet';
import { BottomNavigation } from '@/components/dashboard/BottomNavigation';
import { SeatUpgradeBanner } from '@/components/dashboard/SeatUpgradeBanner';
import { ExecutionTracker } from '@/components/dashboard/ExecutionTracker';
import { InsightsPanel } from '@/components/dashboard/InsightsPanel';
import { SystemBootOverlay } from '@/components/dashboard/SystemBootOverlay';
import { TeamActivityFeed } from '@/components/dashboard/TeamActivityFeed';
import { AuditTrailPanel } from '@/components/dashboard/AuditTrailPanel';
import { OutcomeCheckSection } from '@/components/dashboard/OutcomeCheckSection';
import { ReviewSection } from '@/components/dashboard/ReviewSection';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  SessionsListSkeleton,
  DashboardHeaderSkeleton,
} from '@/components/dashboard/skeletons/DashboardSkeletons';
import quentariLogo from '@/assets/quentari-logo.svg';
import { Loader2, Users, Command, Settings, ChevronDown, BarChart3, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDashboardPreferences } from '@/hooks/useDashboardPreferences';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [checkingWorkspace, setCheckingWorkspace] = useState(true);
  const [showNewDecisionSheet, setShowNewDecisionSheet] = useState(false);
  const [showBootOverlay, setShowBootOverlay] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showMobileInsights, setShowMobileInsights] = useState(false);
  const [activeBottomTab, setActiveBottomTab] = useState('home');
  const [analyseOpen, setAnalyseOpen] = useState(false);
  const [teamDefaultTab, setTeamDefaultTab] = useState('members');
  const [teamDefaultRecipientId, setTeamDefaultRecipientId] = useState<string | undefined>(undefined);

  const { personalization } = useSeatPersonalization(
    workspaceId,
    user?.id || null
  );

  const { stats, activeSessions, teamActivities, userProfile, seatInfo, loading: dashboardLoading, error: dashboardError, refetch } = useDashboardData(workspaceId);
  
  const {
    preferences,
    updateActiveInsightsTab,
  } = useDashboardPreferences();

  // Check workspace membership
  useEffect(() => {
    let cancelled = false;
    
    async function checkWorkspace() {
      if (authLoading) return;
      
      if (!user) {
        setCheckingWorkspace(false);
        navigate('/');
        return;
      }

      try {
        const { data: memberships } = await supabase
          .from('workspace_members')
          .select('workspace_id, personalization_completed_at')
          .eq('user_id', user.id)
          .eq('seat_status', 'active');

        if (cancelled) return;

        if (memberships && memberships.length > 0) {
          const wsId = memberships[0].workspace_id;
          setWorkspaceId(wsId);
          if (!memberships[0].personalization_completed_at) {
            navigate('/onboarding');
            return;
          }

          const { count } = await supabase
            .from('decision_participants')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id);

          if (cancelled) return;

          if (count === 0) {
            navigate('/welcome', { replace: true });
            return;
          }
        } else {
          navigate('/onboarding');
          return;
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error checking workspace:', error);
        navigate('/onboarding');
      } finally {
        if (!cancelled) setCheckingWorkspace(false);
      }
    }

    checkWorkspace();
    return () => { cancelled = true; };
  }, [user, authLoading, navigate]);

  // Check if this is first visit
  useEffect(() => {
    if (workspaceId && userProfile) {
      const hasSeenWelcome = localStorage.getItem(`welcome-seen-${user?.id}`);
      if (!hasSeenWelcome) {
        setShowWelcome(true);
      }
    }
  }, [workspaceId, userProfile, user?.id]);

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    if (user?.id) {
      localStorage.setItem(`welcome-seen-${user.id}`, 'true');
    }
  };

  const handleStartDecision = () => {
    setShowBootOverlay(true);
  };

  const handleBootComplete = () => {
    setShowBootOverlay(false);
    setShowNewDecisionSheet(true);
  };

  if (authLoading || checkingWorkspace || !workspaceId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
      </div>

      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-14 sm:h-16 items-center justify-between max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src={quentariLogo}
              alt="Quentari"
              className="h-6 sm:h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105 flex-shrink-0"
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-5 sm:h-6 w-px bg-border hidden xs:block" aria-hidden="true" />
            <div className="hidden xs:block">
              <WorkspaceAvatar size="sm" showName={false} />
            </div>
            <div className="hidden sm:block">
              <WorkspaceAvatar size="sm" showName />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
                    document.dispatchEvent(event);
                  }}
                  className="hidden sm:flex border-border/50 hover:bg-primary/5 hover:border-primary/20 text-muted-foreground hover:text-primary gap-2"
                >
                  <Command className="h-3.5 w-3.5" />
                  <span className="text-xs">⌘K</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Schnellzugriff öffnen</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowTeamManagement(true)}
                  className="hidden sm:flex border-border/50 hover:bg-primary/5 hover:border-primary/20 text-muted-foreground hover:text-primary h-8 w-8"
                >
                  <Users className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Team</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/settings/workspace')}
                  className="hidden sm:flex hover:bg-primary/5 text-muted-foreground hover:text-primary h-8 w-8"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Einstellungen</p></TooltipContent>
            </Tooltip>
            <UserMenu
              onOpenTeamMessages={(recipientId) => {
                setTeamDefaultTab('messages');
                setTeamDefaultRecipientId(recipientId);
                setShowTeamManagement(true);
              }}
              onOpenAccessRequests={() => {
                setTeamDefaultTab('members');
                setShowTeamManagement(true);
              }}
            />
          </div>
        </div>
      </header>

      <main className="container max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 relative z-10 pb-24 lg:pb-8">
        {seatInfo && (
          <SeatUpgradeBanner usedSeats={seatInfo.usedSeats} maxSeats={seatInfo.maxSeats} />
        )}

        {/* Commitment-Status Header */}
        <div className="stagger-1">
          {dashboardLoading ? (
            <DashboardHeaderSkeleton />
          ) : (
            <DashboardHeader
              userName={userProfile?.first_name || userProfile?.name || user?.email?.split('@')[0] || null}
              stats={stats}
              onNewDecision={handleStartDecision}
            />
          )}
        </div>

        {/* Outcome Check */}
        {!dashboardLoading && workspaceId && user && (
          <div className="stagger-2 space-y-4">
            <OutcomeCheckSection workspaceId={workspaceId} userId={user.id} />
            <ReviewSection workspaceId={workspaceId} userId={user.id} />
          </div>
        )}

        {/* Attention Queue */}
        {!dashboardLoading && !dashboardError && activeSessions.length > 0 && (
          <div className="stagger-2">
            <AttentionQueue sessions={activeSessions} />
          </div>
        )}

        {/* Decision Commitment Table */}
        <div className="stagger-3">
          {dashboardError ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-foreground mb-1">Daten konnten nicht geladen werden</h3>
              <p className="text-xs text-muted-foreground mb-4">Bitte versuche es erneut.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Erneut laden
              </Button>
            </div>
          ) : dashboardLoading ? (
            <SessionsListSkeleton />
          ) : (
            <ActiveSessionsList 
              sessions={activeSessions} 
              onNewDecision={handleStartDecision}
            />
          )}
        </div>

        {/* Execution Tracker - always visible */}
        <div className="mt-6 stagger-3">
          <ExecutionTracker workspaceId={workspaceId} isLoading={dashboardLoading} />
        </div>

        {/* Analyse Section - collapsible, below the list */}
        <div className="mt-6 stagger-3">
          <Collapsible open={analyseOpen} onOpenChange={setAnalyseOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-border/50 bg-card hover:bg-muted/30 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Analyse
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${analyseOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3">
              <DashboardSidebar
                sessions={activeSessions}
                teamActivities={teamActivities}
                workspaceId={workspaceId}
                isLoading={dashboardLoading}
                activeInsightsTab={preferences.activeInsightsTab}
                onInsightsTabChange={updateActiveInsightsTab}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      </main>

      <SystemBootOverlay active={showBootOverlay} onComplete={handleBootComplete} />

      {workspaceId && (
        <NewDecisionSheet
          open={showNewDecisionSheet}
          onOpenChange={setShowNewDecisionSheet}
          workspaceId={workspaceId}
          personalization={personalization || {
            job_role: null,
            job_title: null,
            preferred_decision_areas: [],
            team_size: null,
            company_phase: null,
            decision_style: [],
            communication_preference: null,
            detail_level: null,
            current_priorities: [],
            personalization_completed_at: null,
          }}
          firstName={userProfile?.first_name}
        />
      )}

      {userProfile && (
        <WelcomeDialog
          open={showWelcome}
          onClose={handleCloseWelcome}
          firstName={userProfile.first_name || ''}
          onStartDecision={handleStartDecision}
          onTeamManagement={() => setShowTeamManagement(true)}
        />
      )}

      {workspaceId && user && (
        <>
          <NewAssignmentsDialog userId={user.id} workspaceId={workspaceId} />
          <TeamManagementSheet
            open={showTeamManagement}
            onOpenChange={(open) => {
              setShowTeamManagement(open);
              if (!open) {
                setTeamDefaultTab('members');
                setTeamDefaultRecipientId(undefined);
              }
            }}
            workspaceId={workspaceId}
            userId={user.id}
            defaultTab={teamDefaultTab}
            defaultRecipientId={teamDefaultRecipientId}
          />
        </>
      )}


      <BottomNavigation
        activeTab={activeBottomTab}
        onTabChange={(tab) => {
          setActiveBottomTab(tab);
          if (tab === 'home') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (tab === 'team') {
            setShowTeamManagement(true);
          } else if (tab === 'insights') {
            setShowMobileInsights(true);
          }
        }}
        onNewDecision={handleStartDecision}
      />

      {/* Mobile Insights Sheet */}
      <Sheet open={showMobileInsights} onOpenChange={setShowMobileInsights}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl overflow-hidden">
          <SheetHeader className="pb-4 border-b border-border/50">
            <SheetTitle className="text-lg font-semibold">Analyse</SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              Muster, Statistiken und Empfehlungen
            </SheetDescription>
          </SheetHeader>
          <div className="overflow-y-auto h-[calc(85vh-100px)] pt-4">
            <Tabs defaultValue="insights" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="insights">Insights</TabsTrigger>
                <TabsTrigger value="team">Team</TabsTrigger>
                <TabsTrigger value="audit">Audit</TabsTrigger>
              </TabsList>
              
              <TabsContent value="insights" className="mt-0">
                <InsightsPanel 
                  sessions={activeSessions} 
                  activeTab={preferences.activeInsightsTab}
                  onTabChange={updateActiveInsightsTab}
                  embedded
                />
              </TabsContent>
              
              <TabsContent value="team" className="mt-0">
                <TeamActivityFeed activities={teamActivities} embedded />
              </TabsContent>

              <TabsContent value="audit" className="mt-0">
                {workspaceId && (
                  <AuditTrailPanel workspaceId={workspaceId} embedded />
                )}
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Dashboard;
